/**
 * ProfileContactSheet (web): Gemini-styled alert dialog.
 *
 * Pure HTML + CSS variables. Mirrors the Social sheet pattern (centered modal,
 * neutral icon discs, white pill inputs, sentence-case typography) so the two
 * surfaces feel like sibling alerts in the same design language.
 */
import React, { useCallback, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import {
  PUBLIC_PROFILE_CONTACT_ORDER,
  isBuiltInContactChannel,
  type PublicProfileContactChannelId,
} from '../../lib/public-profile-builder';
import {
  getContactLinkField,
  getLinkErrorKey,
  getLinkPlaceholder,
} from '../../lib/profile-link-fields';
import { ProfileLinkInput } from './ProfileLinkInput.web';
import { ProfileBrandIcon, type ProfileIconId } from './ProfileIconRegistry';

function toBrandIcon(id: PublicProfileContactChannelId): ProfileIconId {
  if (id === 'tabbioMessage') return 'tabbio';
  if (id === 'whatsapp') return 'whatsapp';
  if (id === 'telegram') return 'telegram';
  if (id === 'slack') return 'slack';
  if (id === 'email') return 'email';
  if (id === 'landline') return 'landline';
  return 'phone';
}

export function ProfileContactSheet() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { contacts, routeMode, toggleContact, updateContactValue } =
    usePublicProfileBuilderModel();
  const tx = useCallback((key: string) => String(t(key as never)), [t]);

  const items = Array.isArray(contacts) ? contacts : [];
  const profileRoutes = getPublicProfileRoutes(routeMode);

  const closeDialog = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  return (
    <WebDialog
      title={tx('publicProfile.contactSheet.title')}
      subtitle={tx('publicProfile.contactSheet.subtitle')}
      onClose={closeDialog}
      maxWidth={560}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {(PUBLIC_PROFILE_CONTACT_ORDER ?? []).map((id) => {
          const channel = items.find((item) => item.id === id);
          const enabled = channel?.enabled ?? false;
          const value = channel?.value ?? '';

          const builtIn = isBuiltInContactChannel(id);

          return (
            <ContactRow
              key={id}
              id={id}
              enabled={enabled}
              value={value}
              labelKey={`publicProfile.contacts.${id}.label`}
              descKey={`publicProfile.contacts.${id}.desc`}
              tx={tx}
              addLabel={tx(builtIn ? 'actions.enable' : 'actions.add')}
              removeLabel={tx(builtIn ? 'actions.disable' : 'actions.remove')}
              destructive={!builtIn}
              editable={!builtIn}
              onToggle={() => toggleContact(id)}
              onChange={(next) => updateContactValue(id, next)}
            />
          );
        })}
      </div>
    </WebDialog>
  );
}

interface ContactRowProps {
  id: PublicProfileContactChannelId;
  enabled: boolean;
  value: string;
  labelKey: string;
  descKey: string;
  tx: (key: string) => string;
  addLabel: string;
  removeLabel: string;
  destructive: boolean;
  /** Built-in channels carry no address, so they show no input. */
  editable: boolean;
  onToggle: () => void;
  onChange: (next: string) => void;
}

function ContactRow({
  id,
  enabled,
  value,
  labelKey,
  descKey,
  tx,
  addLabel,
  removeLabel,
  destructive,
  editable,
  onToggle,
  onChange,
}: ContactRowProps) {
  const [hover, setHover] = useState(false);
  const field = getContactLinkField(id);
  const activeColor = destructive ? '#d93025' : 'var(--text-primary)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 4px',
        borderBottom: '1px solid var(--gem-outline-variant)',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 20,
          background: 'rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
        }}
      >
        <ProfileBrandIcon id={toBrandIcon(id)} size={18} color="currentColor" />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: '20px',
            color: 'var(--text-primary)',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {tx(labelKey)}
        </div>
        {enabled && editable ? (
          <ProfileLinkInput
            field={field}
            value={value}
            placeholder={getLinkPlaceholder(field, tx)}
            errorText={tx(getLinkErrorKey(field, id))}
            onCommit={onChange}
          />
        ) : (
          <div
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--text-tertiary)',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {tx(descKey)}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          flexShrink: 0,
          height: 32,
          minWidth: 64,
          padding: '0 12px',
          borderRadius: 9999,
          border: 'none',
          background: hover ? 'rgba(0,0,0,0.06)' : 'transparent',
          color: enabled ? activeColor : 'var(--text-primary)',
          fontSize: 13,
          fontWeight: 600,
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          cursor: 'pointer',
          transition: 'background-color 140ms',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <MaterialSymbol
          name={enabled ? (destructive ? 'close' : 'remove') : 'add'}
          size={16}
        />
        {enabled ? removeLabel : addLabel}
      </button>
    </div>
  );
}
